import DataLoader from 'dataloader';
import { HTTPMethod } from 'utils/HTTPUtils';
import { authenticationHeaders } from 'networking/mindfeed/utils/authenticationHeaders';
import { routes } from 'networking/mindfeed/constants/api';
import { apiFetch, isApiError } from 'networking/mindfeed/utils/apiClient';
import type { BoxId } from 'models/Box';
import type { MediaAsset, MediaId, MediaType } from 'models/Media';
import type { MediaPage } from 'networking/mindfeed/models/Media';
import type { UserId } from 'models/User';

// What the media is for — mirrors the mobile `MediaUploadIntent`: a file for
// a new memo, or the user's own profile photo.
export type MediaUploadIntent =
  | { type: 'newMemoMedia'; context: { memoryBoxId: BoxId } }
  | { type: 'userProfileImage'; context: { userId: UserId } };

export const newMemoMediaIntent = (boxId: BoxId): MediaUploadIntent => ({
  type: 'newMemoMedia',
  context: { memoryBoxId: boxId },
});

type SignedUploadUrl = {
  mediaId: string;
  uploadUrl: string;
};

// Step 1: ask the API for a one-time signed URL + the id the media will have.
const requestSignedUploadUrl = async (params: {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  intent: MediaUploadIntent;
}): Promise<SignedUploadUrl> =>
  apiFetch<SignedUploadUrl>(routes.media.signedUploadUrl(), {
    method: HTTPMethod.POST,
    headers: await authenticationHeaders(),
    body: JSON.stringify(params),
  });

// Step 2: PUT the bytes straight to the signed (storage) URL. This is NOT our
// API — no auth header — just the file and the media-id metadata header the
// backend expects (matching the mobile client).
const uploadToSignedUrl = async (
  uploadUrl: string,
  file: File | Blob,
  mediaId: string,
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: HTTPMethod.PUT,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-goog-meta-media-id': mediaId,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Media upload failed: ${response.status}`);
  }
};

/**
 * Upload one file for a new memo and resolve to its media id (used as an
 * `imageIds` / `audioIds` entry on memo creation).
 */
export const uploadMemoMedia = async (
  boxId: BoxId,
  file: File,
): Promise<string> => {
  const { mediaId, uploadUrl } = await requestSignedUploadUrl({
    filename: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    intent: newMemoMediaIntent(boxId),
  });

  await uploadToSignedUrl(uploadUrl, file, mediaId);
  return mediaId;
};

/**
 * Upload a profile photo for the signed-in user and resolve to its media id.
 * The photo isn't shown until the id is saved on the user (setProfileImage).
 */
export const uploadProfileImage = async (
  userId: UserId,
  image: Blob,
  filename: string,
): Promise<MediaId> => {
  const { mediaId, uploadUrl } = await requestSignedUploadUrl({
    filename,
    mimeType: image.type,
    sizeBytes: image.size,
    intent: { type: 'userProfileImage', context: { userId } },
  });

  await uploadToSignedUrl(uploadUrl, image, mediaId);
  return mediaId;
};

/**
 * Get Media by ids for up to 25 media at once.
 * Will also return signed CDN URLs.
 */
export const getMediaByIds = async (
  mediaIds: MediaId[],
): Promise<MediaAsset[]> =>
  apiFetch<{ data: MediaAsset[] }>(routes.media.byIds(mediaIds), {
    method: HTTPMethod.GET,
    headers: await authenticationHeaders(),
  }).then((json) => json.data);

/**
 * Looks up a list of ids, giving back one result per id: the media.
 */
const loadMedia = async (
  ids: readonly MediaId[],
): Promise<(MediaAsset | Error)[]> => {
  try {
    const assets = await getMediaByIds([...new Set(ids)]);
    const byId = new Map(assets.map((asset) => [asset.id, asset]));
    return ids.map((id) => byId.get(id) ?? new Error(`Media ${id} not found`));
  } catch (error) {
    // The API refuses the whole request if the user can't see even one of the
    // ids. Ask for them one at a time so the others still load.
    if (ids.length > 1 && isApiError(error) && error.status === 403) {
      return Promise.all(
        ids.map((id) =>
          loadMedia([id]).then(
            ([result]) => result,
            (reason) => reason as Error,
          ),
        ),
      );
    }
    throw error;
  }
};

// Ids asked for within 50ms of each other go out as one request. Caching is
// left to React Query (see useMedia).
const mediaLoader = new DataLoader(loadMedia, {
  maxBatchSize: 25, // the API ignores ids past the 25th
  batchScheduleFn: (callback) => setTimeout(callback, 50),
  cache: false,
});

/** One media file with its signed CDN URLs, fetched together with others. */
export const getMedia = (mediaId: MediaId): Promise<MediaAsset> =>
  mediaLoader.load(mediaId);

// Grid tiles are a third of a memo card's width, so a page holds twice the
// memos' 30 to fill more than one screen.
export const BOX_MEDIA_PAGE_LIMIT = 60;

// GET /boxes/{id}/media?media_type=… — the box's media of one kind, one
// cursor-paginated page at a time.
export const getBoxMediaPage = async ({
  boxId,
  type,
  after,
  limit = BOX_MEDIA_PAGE_LIMIT,
}: {
  boxId: BoxId;
  type: MediaType;
  after?: string | null;
  limit?: number;
}): Promise<MediaPage> => {
  const params = new URLSearchParams({
    limit: String(limit),
    media_type: type,
  });
  if (after) params.set('after', after);

  const response = await fetch(
    `${routes.boxes.media(boxId)}?${params.toString()}`,
    {
      method: HTTPMethod.GET,
      headers: await authenticationHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`Unable to fetch box media: error ${response.status}`);
  }

  const json = (await response.json()) as { data: MediaAsset[] };
  const count = response.headers.get('x-pagination-count');

  return {
    items: json.data,
    cursorAfter: response.headers.get('x-pagination-cursor-after') || null,
    totalCount: count ? Number(count) : null,
  };
};
